import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Globe, RefreshCw, Clock, Zap, BarChart3, DollarSign, ArrowUpRight, ArrowDownRight, Tag } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { getRiskBadge, getTimeAgo, getOutlookColor, getOutlookIcon } from "./helpers";
import { QuickDetailModal, buildStockDetail } from "./QuickDetailModal";
import { useState, useEffect } from "react";

export const StocksTab = ({
  stockTrends, stocksLastRefresh, stocksLoading, stocksRegionFilter, setStocksRegionFilter,
  eurUsdHistory, handleStocksRefresh, saveToMyDeals, getPortfolioFit, isDemo, t, userPreferredRegions = [],
}) => {
  // Map user-friendly region names to stock API codes
  const regionNameToCode = {
    "US": "US",
    "Europe": "EU",
    "Asia-Pacific": "APAC",
    "Emerging Markets": "EM",
  };
  
  // Convert user preferred regions to API codes
  const preferredCodes = userPreferredRegions.map(r => regionNameToCode[r] || r);
  
  // Filter regions - if "all", show all; otherwise filter by code OR by whether it's in preferred regions
  const filteredRegions = stockTrends?.regions?.filter((r) => {
    if (stocksRegionFilter === "all") return true;
    if (stocksRegionFilter === "__preferred__") return preferredCodes.includes(r.code);
    return r.code === stocksRegionFilter;
  }) || [];

  const [detailPick, setDetailPick] = useState(null);
  const [fitCache, setFitCache] = useState({});

  // Compute fit scores once when trends load
  useEffect(() => {
    if (!stockTrends?.regions?.length) return;
    const picks = stockTrends.regions.flatMap(r => r.sectors?.flatMap(s => s.picks || []) || []);
    const uniqueRisks = [...new Set(picks.map(p => p.risk_score || 50))];

    const fetchFits = async () => {
      const cache = {};
      for (const risk of uniqueRisks) {
        const fit = await getPortfolioFit({ risk_score: risk });
        cache[risk] = fit;
      }
      setFitCache(cache);
    };
    fetchFits();
  }, [stockTrends]);

  const getFit = (pick) => fitCache[pick.risk_score || 50] || { label: "...", color: "text-muted-foreground", desc: "" };

  return (
    <>
      <div className="flex items-center justify-between" data-testid="stocks-refresh-bar">
        <div className="flex items-center gap-3">
          <Button onClick={handleStocksRefresh} disabled={stocksLoading} data-testid="refresh-stocks-btn" className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider text-xs">
            <RefreshCw className={`w-4 h-4 mr-2 ${stocksLoading ? "animate-spin" : ""}`} />
            {stocksLoading ? t("opps.analyzing") : t("opps.refreshTrends")}
          </Button>
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Clock className="w-3.5 h-3.5" />
            <span>Last refresh: <span className="font-mono font-medium text-foreground">{getTimeAgo(stocksLastRefresh)}</span></span>
          </div>
        </div>
        <Select value={stocksRegionFilter} onValueChange={setStocksRegionFilter}>
          <SelectTrigger className="w-52" data-testid="stocks-region-filter"><Globe className="w-4 h-4 mr-2" /><SelectValue placeholder="All Regions" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Regions</SelectItem>
            {preferredCodes.length > 0 && preferredCodes.length < 4 && (
              <SelectItem value="__preferred__">
                ★ My Preferred ({preferredCodes.length})
              </SelectItem>
            )}
            {stockTrends?.regions?.map((r) => (
              <SelectItem key={r.code} value={r.code}>
                {r.name} ({r.currency})
                {preferredCodes.includes(r.code) && " ★"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {eurUsdHistory.length > 0 && (
        <Card data-testid="eur-usd-chart-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xs font-bold tracking-widest uppercase text-muted-foreground/80">{t("opps.eurUsdChart")}</CardTitle>
              <div className="flex items-center gap-2 text-sm">
                <DollarSign className="w-4 h-4 text-primary" />
                <span className="font-mono font-bold">{eurUsdHistory[eurUsdHistory.length - 1]?.rate?.toFixed(4)}</span>
                {eurUsdHistory.length > 1 && (() => {
                  const change = eurUsdHistory[eurUsdHistory.length - 1].rate - eurUsdHistory[0].rate;
                  const pct = ((change / eurUsdHistory[0].rate) * 100).toFixed(2);
                  return (<span className={`flex items-center text-xs ${change >= 0 ? "text-green-500" : "text-red-500"}`}>{change >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}{pct > 0 ? "+" : ""}{pct}%</span>);
                })()}
              </div>
            </div>
          </CardHeader>
          <CardContent className="pb-4">
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={eurUsdHistory} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                  <defs><linearGradient id="eurUsdGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} /><stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(d) => { const [,m,day] = d.split("-"); return `${m}/${day}`; }} interval="preserveStartEnd" minTickGap={60} />
                  <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} domain={["auto", "auto"]} tickFormatter={(v) => v.toFixed(2)} width={45} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px", fontSize: "12px" }} formatter={(v) => [`${v.toFixed(4)}`, "EUR/USD"]} labelFormatter={(l) => `Date: ${l}`} />
                  <Area type="monotone" dataKey="rate" stroke="hsl(var(--primary))" fill="url(#eurUsdGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {stockTrends?.themes?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="investment-themes">
          {stockTrends.themes.map((theme, i) => (
            <Card key={i} className="border-primary/20">
              <CardContent className="pt-4 pb-3 space-y-1.5">
                <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-primary flex-shrink-0" /><h4 className="font-bold text-xs truncate">{theme.name}</h4></div>
                <p className="text-[11px] text-muted-foreground line-clamp-2">{theme.description}</p>
                <div className="flex flex-wrap gap-1 pt-1">{theme.related_tickers?.map((tk) => (<Badge key={tk} variant="outline" className="text-[9px] font-mono">{tk}</Badge>))}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {filteredRegions.length > 0 ? (
        <div className="space-y-6" data-testid="stock-trends-regions">
          {filteredRegions.map((region, ri) => (
            <Card key={ri} data-testid={`region-card-${region.code}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-primary" />
                    <div><CardTitle className="text-base font-bold">{region.name}</CardTitle><p className="text-xs text-muted-foreground">{region.summary}</p></div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="font-mono text-xs">{region.currency}</Badge>
                    <Badge className={`${getOutlookColor(region.outlook)} bg-transparent border text-xs`}>{getOutlookIcon(region.outlook)}<span className="ml-1">{region.outlook}</span></Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {region.sectors?.map((sector, si) => (
                  <div key={si} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2"><h4 className="text-sm font-bold">{sector.name}</h4><Badge variant="outline" className="text-[10px]">{sector.trend}</Badge></div>
                      {sector.news_theme && <p className="text-[11px] text-muted-foreground italic max-w-sm truncate">{sector.news_theme}</p>}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {sector.picks?.map((pick, pi) => (
                        <div key={pi} className="p-3 bg-secondary/40 rounded-lg border border-border/50 hover:border-primary/30 transition-colors cursor-pointer" data-testid={`stock-pick-${pick.ticker}`} onClick={() => setDetailPick({ pick, sector, region })}>
                          <div className="flex items-start justify-between mb-2">
                            <div className="min-w-0 flex-1">
                              <p className="font-bold text-sm truncate">{pick.name}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="font-mono text-xs text-primary font-bold">{pick.ticker}</span>
                                <Badge variant="outline" className="text-[9px]">{pick.type}</Badge>
                                <Badge variant="outline" className="text-[9px] font-mono">{pick.currency}</Badge>
                              </div>
                            </div>
                            {getRiskBadge(pick.risk_score)}
                          </div>
                          <p className="text-[11px] text-muted-foreground mb-2 line-clamp-2">{pick.rationale}</p>
                          <div className="flex items-center justify-between" onClick={(e) => e.stopPropagation()}>
                            <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> {pick.horizon}</span>
                            <div className="flex items-center gap-2">
                              {!isDemo && (<Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1 px-2" onClick={() => saveToMyDeals({name: pick.name, token: pick.ticker, description: pick.rationale, risk_score: pick.risk_score, currency: pick.currency, expected_return: pick.horizon, notes: `Sector: ${sector.name}, Region: ${region.name}`}, "stocks")} data-testid={`stock-select-${pick.ticker}`}><Tag className="w-3 h-3" /> Add to MyDeals</Button>)}
                              {(() => { const fit = getFit(pick); return (<span className={`text-[10px] font-bold ${fit.color}`} title={fit.desc} data-testid={`fit-${pick.ticker}`}>{fit.label}</span>); })()}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card><CardContent className="py-16 text-center">
          <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground mb-1">{!stockTrends ? "No stock trends data yet" : "No regions match your filter"}</p>
          <p className="text-sm text-muted-foreground mb-4">{!stockTrends ? 'Click "Refresh Trends" to generate AI-powered investment recommendations' : "Try selecting a different region"}</p>
          {!stockTrends && (<Button onClick={handleStocksRefresh} disabled={stocksLoading} className="bg-primary text-primary-foreground"><RefreshCw className={`w-4 h-4 mr-2 ${stocksLoading ? "animate-spin" : ""}`} />{stocksLoading ? "Analyzing..." : "Refresh Trends"}</Button>)}
        </CardContent></Card>
      )}

      <QuickDetailModal
        isOpen={!!detailPick}
        onOpenChange={(open) => !open && setDetailPick(null)}
        item={detailPick ? buildStockDetail(detailPick.pick, detailPick.sector, detailPick.region) : null}
        type="stock"
        onSave={detailPick ? () => saveToMyDeals({name: detailPick.pick.name, token: detailPick.pick.ticker, description: detailPick.pick.rationale, risk_score: detailPick.pick.risk_score, currency: detailPick.pick.currency, expected_return: detailPick.pick.horizon, notes: `Sector: ${detailPick.sector?.name}, Region: ${detailPick.region?.name}`}, "stocks") : null}
        isDemo={isDemo}
      />
    </>
  );
};
